import { markRaw } from 'vue'
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
  type IPublicClientApplication,
  type SilentRequest,
} from '@azure/msal-browser'
import { defineStore } from 'pinia'
import sCache from '@/plugins/simple-cache'

/**
 * MSAL configuration: the same tenant and client the Vue 2 app used, expressed
 * with @azure/msal-browser 4.
 *
 * Interaction is by redirect, not popup: the whole tab navigates to Microsoft
 * and comes back. A popup is a second window the browser is entitled to block,
 * and on a phone it is a second tab the user has to find. The trade is that any
 * call which needs interaction never returns: the page is gone. Nothing here may
 * start one behind the user's back, which is what `acquire` below is careful about.
 */
const redirectUri = import.meta.env.VITE_MSAL_REDIRECT_URL || ''

const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_MSAL_CLIENT_ID || '',
    authority: import.meta.env.VITE_MSAL_AUTHORITY || '',
    redirectUri,
    postLogoutRedirectUri: import.meta.env.VITE_MSAL_POST_LOGOUT_REDIRECT_URL || `${redirectUri}login`,
    // Come back to the page that started sign-in, not to redirectUri. A session
    // that expires on /entrytime returns to /entrytime.
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
}

const loginRequest = { scopes: ['openid', 'profile'] }
const graphScopes = ['User.Read']
const apiScopes = [`api://${import.meta.env.VITE_MSAL_SCOPE_ID}/access_user`]

/**
 * Set immediately before handing the tab to Microsoft, cleared when a redirect
 * comes back carrying a result. It is what stops a redirect loop: if a silent
 * call still fails after we have already been sent out once, sending the user
 * again would fail again, forever. Sign-in has to be declared broken instead.
 *
 * sessionStorage rather than a field, because the redirect destroys the page.
 */
const INTERACTION_MARKER = 'msal.interactionAttempted'

const interactionAttempted = () => sessionStorage.getItem(INTERACTION_MARKER) === '1'
const markInteraction = () => sessionStorage.setItem(INTERACTION_MARKER, '1')
const clearInteraction = () => sessionStorage.removeItem(INTERACTION_MARKER)

/**
 * The end-to-end tests' stand-in for a signed-in session (see
 * e2e/helpers/mock-session.ts). Honoured only in a Vite dev build: the
 * `import.meta.env.DEV` guard is a compile-time constant, so a production
 * bundle contains none of this and no flag can turn it on.
 */
export const AUTH_MOCK_KEY = 'xguard.authMock'
const MOCK_TOKEN = 'e2e-mock-token'
const mockSession = () => import.meta.env.DEV && sessionStorage.getItem(AUTH_MOCK_KEY) === '1'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    // msal-browser requires an explicit async initialize() before any other call.
    // markRaw keeps Pinia from making the client reactive: it is a class instance
    // with private fields, and proxying it both breaks its typing and is wasteful.
    msal: null as IPublicClientApplication | null,
    initialised: false,
    isAuthenticated: false,
    /** Set when a redirect comes back having failed, for the login page to show. */
    authError: '' as string,
  }),

  actions: {
    /**
     * Creates the MSAL client and settles any redirect that is coming back.
     *
     * This must finish before the router decides anything: on the way back from
     * Microsoft the account exists only inside the response this call processes.
     * main.ts awaits it before mounting for that reason. Safe to call repeatedly.
     */
    async init(): Promise<IPublicClientApplication> {
      if (!this.msal) {
        this.msal = markRaw(new PublicClientApplication(msalConfig))
      }
      if (mockSession()) {
        // Nothing goes to Microsoft; account() and acquire() answer locally.
        this.isAuthenticated = true
        this.initialised = true
        return this.msal
      }
      if (!this.initialised) {
        await this.msal.initialize()

        try {
          // Non-null only when this page load is the return leg of a redirect.
          const result: AuthenticationResult | null = await this.msal.handleRedirectPromise()
          if (result?.account) {
            this.msal.setActiveAccount(result.account)
            clearInteraction()
          }
        } catch (err) {
          // A redirect that came back refused: consent declined, the account is
          // outside the tenant. Recorded rather than rethrown so the login page can say so.
          this.authError = (err as Error).message
          clearInteraction()
        }

        // On an ordinary reload there is no response to process, but the cache
        // may still hold the account; adopt it so the app is not told the user is anonymous.
        if (!this.msal.getActiveAccount()) {
          const cached = this.msal.getAllAccounts()[0]
          if (cached) this.msal.setActiveAccount(cached)
        }

        this.isAuthenticated = Boolean(this.msal.getActiveAccount())
        this.initialised = true
      }
      return this.msal
    },

    /** The signed-in account, or null when nobody is signed in. */
    async account(): Promise<AccountInfo | null> {
      if (mockSession()) return null
      const msal = await this.init()
      return msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null
    },

    /**
     * Starts sign-in. Only ever called from the sign-in button: this navigates
     * the tab away, so it does not return and must never fire on its own.
     */
    async signIn(): Promise<void> {
      if (mockSession()) return
      const msal = await this.init()
      this.authError = ''
      markInteraction()
      try {
        await msal.loginRedirect(loginRequest)
      } catch (err) {
        clearInteraction()
        this.authError = (err as Error).message
        console.warn((err as Error).message)
      }
    },

    async logOut(): Promise<void> {
      sCache.clear('loggedIn')
      clearInteraction()
      this.isAuthenticated = false

      if (mockSession()) {
        sessionStorage.removeItem(AUTH_MOCK_KEY)
        document.location.href = '/login'
        return
      }

      try {
        const msal = await this.init()
        const account = await this.account()
        // Navigates away, so nothing below it runs.
        await msal.logoutRedirect({
          account: account ?? undefined,
          postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
        })
      } catch {
        // Sign the user out of this app no matter what the service said.
        document.location.href = '/login'
      }
    },

    /** Token for Microsoft Graph. */
    async getToken(): Promise<string> {
      const token = await this.acquire(graphScopes)
      this.isAuthenticated = Boolean(token)
      return token
    },

    /** Token for the XGuard API. */
    async getApiToken(): Promise<string> {
      return this.acquire(apiScopes)
    },

    /**
     * Acquires a token silently.
     *
     * Nobody signed in: return empty and let the caller send the user to the
     * login page. Someone signed in but the token needs interaction (consent,
     * MFA, an expired refresh token): redirect, once only (see INTERACTION_MARKER).
     */
    async acquire(scopes: string[]): Promise<string> {
      if (mockSession()) return MOCK_TOKEN
      const msal = await this.init()
      const account = await this.account()

      if (!account) return ''

      const request: SilentRequest = { scopes, account }
      try {
        const response = await msal.acquireTokenSilent(request)
        clearInteraction()
        return response.accessToken
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError && !interactionAttempted()) {
          markInteraction()
          try {
            // Navigates away; the token arrives on the next page load.
            await msal.acquireTokenRedirect({ scopes, account })
          } catch (redirectErr) {
            clearInteraction()
            console.warn((redirectErr as Error).message)
          }
          return ''
        }
        console.warn((err as Error).message)
        return ''
      }
    },
  },
})
